import React, { useState } from 'react';
import { X, Loader2, Download, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/utils';

/**
 * Modal that captures name + email before allowing a free download.
 * Persists the lead on the server, optionally subscribes the user to the newsletter,
 * then triggers the file download.
 */
const LeadCaptureModal = ({ open, product, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subscribe, setSubscribe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!open || !product) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error('Preencha nome e email.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/leads/download/${product.id}`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        subscribe_newsletter: subscribe,
      });
      const url = resolveImageUrl(res.data.download_url);
      // Derive extension from URL
      const ext = (res.data.download_url.match(/\.([a-z0-9]{1,5})$/i)?.[1] || 'pdf').toLowerCase();
      // Trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `${product.name}.${ext}`;
      a.rel = 'noopener noreferrer';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar pedido');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setName(''); setEmail(''); setSubscribe(true); setDone(false); onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
      onClick={reset}
      data-testid="lead-modal"
    >
      <div
        className="bg-white rounded-sm max-w-md w-full p-7 relative shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={reset}
          className="absolute top-3 right-3 p-1.5 text-[#5C5C5C] hover:text-[#1A1A1A] rounded-full hover:bg-[#E9E5D6] transition-colors"
          aria-label="Fechar"
          data-testid="lead-close-btn"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-[#0F5A34]" />
            </div>
            <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2">
              Download iniciado!
            </h3>
            <p className="text-[#5C5C5C] font-['Inter'] text-sm mb-5">
              Se o download não começar automaticamente, clique no botão abaixo.
            </p>
            <a
              href={resolveImageUrl(product.file_url)}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#0F5A34] hover:bg-[#0c4628] text-white px-5 py-2.5 rounded-sm font-['Montserrat'] text-sm font-medium"
              data-testid="lead-redownload-btn"
            >
              <Download size={16} /> Descarregar de novo
            </a>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <span className="inline-block bg-[#DFAF3A] text-[#1A1A1A] font-['Montserrat'] text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm mb-3">
                Gratuito
              </span>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2 leading-tight">
                {product.name}
              </h3>
              <p className="text-[#5C5C5C] font-['Inter'] text-sm leading-relaxed">
                {product.description}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase text-[#5C5C5C] mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="O seu nome"
                  className="w-full px-3 py-2 border border-[#D6D2C4] rounded-sm bg-white focus:outline-none focus:border-[#0F5A34] text-sm"
                  data-testid="lead-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase text-[#5C5C5C] mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  className="w-full px-3 py-2 border border-[#D6D2C4] rounded-sm bg-white focus:outline-none focus:border-[#0F5A34] text-sm"
                  data-testid="lead-email-input"
                />
              </div>
              <label className="flex items-start gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={subscribe}
                  onChange={(e) => setSubscribe(e.target.checked)}
                  className="mt-1 cursor-pointer"
                  data-testid="lead-subscribe-checkbox"
                />
                <span className="text-xs text-[#5C5C5C] font-['Inter'] leading-relaxed">
                  Quero receber novidades, guias e dicas sobre Portugal por email.
                </span>
              </label>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E53935] hover:bg-[#C62828] disabled:opacity-50 text-white py-2.5 rounded-sm font-['Montserrat'] text-sm font-medium uppercase tracking-wide flex items-center justify-center gap-2 transition-colors"
                data-testid="lead-submit-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {loading ? 'A processar...' : 'Quero baixar agora'}
              </button>
              <p className="text-[11px] text-[#5C5C5C] text-center pt-1">
                Os seus dados ficam connosco. Sem spam. Pode cancelar a inscrição a qualquer momento.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadCaptureModal;
